// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/lib/utils'

type AlertVariant = 'info' | 'success' | 'warning' | 'error'

const variants: Record<AlertVariant, { bg: string; icon: React.ReactNode }> = {
  info:    {
    bg:   'bg-status-infoBg border-sage-200 text-status-infoText',
    icon: <Info className="h-4 w-4 flex-shrink-0" />,
  },
  success: {
    bg:   'bg-status-successBg border-status-successBorder text-status-successText',
    icon: <CheckCircle className="h-4 w-4 flex-shrink-0" />,
  },
  warning: {
    bg:   'bg-status-warnBg border-status-warnBorder text-status-warnText',
    icon: <AlertTriangle className="h-4 w-4 flex-shrink-0" />,
  },
  error: {
    bg:   'bg-status-alertBg border-status-alertBorder text-status-alertText',
    icon: <AlertCircle className="h-4 w-4 flex-shrink-0" />,
  },
}

interface AlertBannerProps {
  variant?:     AlertVariant
  title?:       string
  message:      string
  dismissible?: boolean
  className?:   string
}

export function AlertBanner({
  variant   = 'info',
  title,
  message,
  dismissible,
  className,
}: AlertBannerProps) {
  const [dismissed, setDismissed] = useState(false)
  if (dismissed) return null

  const { bg, icon } = variants[variant]

  return (
    <div
      role="alert"
      className={cn(
        'flex items-start gap-3 rounded-xl border px-4 py-3.5 text-sm',
        bg,
        className,
      )}
    >
      {icon}
      <div className="flex-1">
        {title && <p className="font-semibold mb-0.5">{title}</p>}
        <p className="leading-snug">{message}</p>
      </div>
      {dismissible && (
        <button
          onClick={() => setDismissed(true)}
          className="flex-shrink-0 opacity-60 hover:opacity-100"
          aria-label="Sluiten"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  )
}
